import type { Asset } from "@storytell/types";
import {
  FaSolidFileAudio,
  FaSolidFileCsv,
  FaSolidFileExcel,
  FaSolidFileImage,
  FaSolidFilePdf,
  FaSolidFileVideo,
} from "solid-icons/fa";
import { TbFileText } from "solid-icons/tb";

type AssetType = "pdf" | "spreadsheet" | "image" | "audio" | "video" | "default";

export const getAssetType = (asset: Pick<Asset, "filename">): AssetType => {
  const extension = asset.filename.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "pdf":
      return "pdf";
    case "csv":
    case "xlsx":
    case "xls":
      return "spreadsheet";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
    case "svg":
      return "image";
    case "mp3":
    case "m4a":
    case "wav":
    case "ogg":
    case "opus":
    case "flac":
    case "m4b":
    case "aiff":
    case "au":
      return "audio";
    case "mp4":
    case "mov":
    case "avi":
    case "flv":
    case "3gp":
    case "m4v":
    case "ogm":
    case "ogv":
    case "qt":
    case "webm":
      return "video";
    default:
      return "default";
  }
};

// Colors for each asset type: light mode / dark mode
const assetIconColors: Record<AssetType, string> = {
  pdf: "text-[#dc2626] dark:text-[#f87171]", // Red
  spreadsheet: "text-[#16a34a] dark:text-[#4ade80]", // Green
  image: "text-[#9333ea] dark:text-[#c084fc]", // Purple
  audio: "text-[#ea580c] dark:text-[#fb923c]", // Orange
  video: "text-[#2563eb] dark:text-[#60a5fa]", // Blue
  default: "text-[#6b7280] dark:text-[#9ca3af]", // Gray
};

export const getAssetIconColor = (asset: Pick<Asset, "filename">): string => {
  return assetIconColors[getAssetType(asset)];
};

export const getAssetIcon = (asset: Pick<Asset, "filename">) => {
  const extension = asset.filename.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "pdf":
      return FaSolidFilePdf;
    case "csv":
      return FaSolidFileCsv;
    case "xlsx":
    case "xls":
      return FaSolidFileExcel;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
    case "svg":
      return FaSolidFileImage;
    case "mp3":
    case "m4a":
    case "wav":
    case "ogg":
    case "opus":
    case "flac":
    case "m4b":
    case "aiff":
    case "au":
      return FaSolidFileAudio;
    case "mp4":
    case "mov":
    case "avi":
    case "flv":
    case "3gp":
    case "m4v":
    case "ogm":
    case "ogv":
    case "qt":
    case "webm":
      return FaSolidFileVideo;
    default:
      return TbFileText;
  }
};
